/**
 * Data loading and calculations for the job cost report, moved verbatim out of `CostReportCSV`
 * so the legacy page and the project record's desktop cost report share the same requests and
 * figures. It loads once on mount (event first, then the report data).
 */
import {
  ClassCode,
  CostReportReq,
  getPropertyAddress,
  Int32,
  Invoice,
  NULL_TIME,
  type OrderDir,
  type Payment,
  PerDiem,
  PerDiemRow,
  type Quotable,
  QuotableRead,
  Reimbursement,
  Task,
  TimesheetLine,
  Transaction,
  TransactionAccount,
  Trip,
  User,
} from '@kalos/kalos-rpc';
import { toast } from '@kalos/ui';
import { differenceInMinutes, endOfWeek, format, parseISO, startOfWeek } from 'date-fns';
import { useCallback, useEffect, useReducer, useState } from 'react';

import { IRS_SUGGESTED_MILE_FACTOR } from '../../../constants';
import { useDownloadTransactionFilesZipMutation } from '../../../hooks/react-query/useS3Query';
import {
  ClassCodeClientService,
  downloadCSV,
  EventClientService,
  formatDate,
  InvoiceClientService,
  PaymentClientService,
  ReimbursementClientService,
  roundNumber,
  TimesheetDepartmentClientService,
  TransactionAccountClientService,
  usd,
  UserClientService,
} from '../../../tools/helpers';
import { REIMBURSEMENT_STATUSES } from '../../ReimbursementLog/components/constants';
import { type DataList } from '../Timesheet/reducer';
import { ACTIONS, type DateSortTab, reducer, type WeekClassCodeBreakdownSubtotal } from './reducer';
import { calculateWeeklySummary } from './timesheetSummary';

export type Week = {
  weekStart: string;
  weekEnd: string;
  code: number;
};

// Cost center 643003 = "Meals and Entertainment-Travel/Transportation" ("Job Travel"), the
// account company-booked hotel/lodging spend is reconciled under. It is reported under Lodging
// (not generic Transactions) so the Job Details Lodging figure reflects real hotel cost, and it
// is excluded from the Transactions total/breakdown so the same dollars are not counted twice.
// (Lodging Requests also record an assignment cost for the same stay, but that is the manually
// entered duplicate of this reconciled spend and is intentionally not summed here.)
export const LODGING_TRAVEL_COST_CENTER_ID = 643003;

export const isLodgingTravelTransaction = (t: Transaction) =>
  t.costCenterId === LODGING_TRAVEL_COST_CENTER_ID;

/** Split job transactions into company-booked lodging (cost center 643003) vs everything else. */
export const getTransactionTotals = (transactions: Transaction[]) => {
  let lodging = 0;
  let nonLodging = 0;
  transactions.forEach((t) => {
    if (isLodgingTravelTransaction(t)) {
      lodging += t.amount;
    } else {
      nonLodging += t.amount;
    }
  });
  return { lodging, nonLodging };
};

/** Sum meals and lodging from per diem rows (use row-level values for accurate job allocation). */
export const getPerDiemTotalsFromRows = (rows: PerDiemRow[]) => ({
  meals: rows.reduce((sum, r) => sum + (r.mealsValue || 0), 0),
  lodging: rows.reduce((sum, r) => sum + (r.lodgingValue || 0), 0),
});

export const tabs = [
  'JobDetails',
  'Invoice',
  'Payments',
  'Transactions',
  'PerDiems',
  'Timesheets',
  'Trips',
  'Spiffs',
  'Reimbursements',
] as const;

export const isNullTime = (value?: string) =>
  !value || value === NULL_TIME || value.startsWith('0001-01-01') || value.startsWith('0000-00-00');

/** Separators a department name can start with when one of its parts is empty: "-Residential". */
const LEADING_SEPARATORS = /^[\s\-\u2013\u2014\u00b7:|,/]+/;

/**
 * A department label for display: its non-empty parts joined by " - ", each without a stray
 * leading separator, so "-Residential - MEP" or an empty classification reads "Residential - MEP".
 */
export const formatDepartmentLabel = (...parts: (string | undefined)[]) =>
  parts
    .map((part) => (part ?? '').replace(LEADING_SEPARATORS, '').trim())
    .filter(Boolean)
    .join(' - ');

export const TRANSACTION_STATUS_LABELS: Record<number, string> = {
  1: 'New',
  2: 'Pending',
  3: 'Accepted',
  4: 'Rejected',
  5: 'Processed',
};
export const TRANSACTION_STATUS_REJECTED = 4;
export const REIMBURSEMENT_STATUS_REJECTED = 4;
/** task_rpc_read.review_status = SpiffToolAdminAction.status of the task's current admin action. */
export const SPIFF_REVIEW_LABELS: Record<number, string> = {
  1: 'Approved',
  2: 'Declined',
  3: 'Revoked',
};
export const SPIFF_REVIEW_DECLINED = 2;
export const SPIFF_REVIEW_REVOKED = 3;

/** Line items written by the AP reconcile flow point at the AP record they duplicate. */
export const isDuplicateTransaction = (t: Transaction) => t.parentTransactionId > 0;
export const getTransactionStatus = (t: Transaction) =>
  isDuplicateTransaction(t)
    ? `Duplicate of AP #${t.parentTransactionId}`
    : TRANSACTION_STATUS_LABELS[t.statusId] || '-';
/** Rejected rows and reconciled duplicates are shown but never summed. */
export const transactionCounts = (t: Transaction) =>
  !isDuplicateTransaction(t) && t.statusId !== TRANSACTION_STATUS_REJECTED;

export const getPerDiemStatus = (pd: PerDiem) =>
  pd.payrollProcessed
    ? 'Processed'
    : !isNullTime(pd.dateApproved)
      ? 'Approved'
      : !isNullTime(pd.dateSubmitted)
        ? 'Submitted'
        : 'Pending';

export const getTimesheetStatus = (tsl: TimesheetLine) =>
  tsl.payrollProcessed
    ? 'Processed'
    : !isNullTime(tsl.adminApprovalDatetime)
      ? 'Approved'
      : !isNullTime(tsl.userApprovalDatetime)
        ? 'Submitted'
        : 'Pending';

export const getTripStatus = (trip: Trip) =>
  trip.payrollProcessed ? 'Processed' : trip.approved ? 'Approved' : 'Pending';

export const getSpiffStatus = (task: Task) =>
  task.payrollProcessed ? 'Processed' : SPIFF_REVIEW_LABELS[task.reviewStatus] || 'Pending';
export const spiffCounts = (task: Task) =>
  task.reviewStatus !== SPIFF_REVIEW_DECLINED && task.reviewStatus !== SPIFF_REVIEW_REVOKED;

export const getReimbursementStatus = (r: Reimbursement) =>
  REIMBURSEMENT_STATUSES.find((s) => s.value === r.statusId)?.label || '-';
export const reimbursementCounts = (r: Reimbursement) =>
  r.statusId !== REIMBURSEMENT_STATUS_REJECTED;

export const compareDates = (a: string, b: string, dir: OrderDir) => {
  const toValue = (value: string) => {
    const parsed = parseISO(value).valueOf();
    return Number.isNaN(parsed) ? 0 : parsed;
  };
  const diff = toValue(a) - toValue(b);
  return dir === 'ASC' ? diff : -diff;
};

export const transformTimesheetsToDataList = (timesheets: TimesheetLine[]): DataList => {
  const dataList: DataList = {};

  timesheets.forEach((timesheet) => {
    if (!timesheet.timeStarted) return;
    const date = timesheet.timeStarted.split(' ')[0];
    if (!dataList[date]) {
      dataList[date] = {
        servicesRenderedList: [],
        timesheetLineList: [],
        payroll: { total: 0, billable: 0, unbillable: 0 },
        timeoffs: [],
      };
    }
    dataList[date].timesheetLineList.push(timesheet);
  });

  return dataList;
};

/** The cost report state, its loaders and the figures `CostReportCSV` renders. */
export const useCostReport = (serviceCallId: number) => {
  // Added for the project record's cost report (not read by CostReportCSV): set when any request
  // below fails, since several of them never settle on failure and the page would load forever.
  const [loadFailed, setLoadFailed] = useState(false);
  const [state, dispatch] = useReducer(reducer, {
    loaded: false,
    loading: true,
    perDiems: [],
    timesheets: [],
    transactions: [],
    users: [],
    costCenterTotals: {},
    loadingEvent: true,
    loadedInit: false,
    event: undefined,
    laborTotals: {},
    transactionSort: { sortBy: 'amount', sortDir: 'ASC' },
    laborTotalsDropDownActive: false,
    employeeHoursDropDownActive: false,
    transactionAccounts: [],
    trips: [],
    costCenterDropDownActive: false,
    tripsTotal: 0,
    tasks: [],
    classCodes: [],
    dropDowns: [],
    transactionDropDowns: [],
    classCodeDropdowns: [],
    timesheetWeeklySubtotals: [],
    totalHoursWorked: 0,
    activeTab: tabs[0],
    printStatus: 'idle' as const,
    openGalleryId: undefined,
    openReconcileId: undefined,
    showEmployeeSummary: false,
    invoice: undefined,
    materials: [],
    payments: [],
    reimbursements: [],
    dateSort: {
      PerDiems: 'DESC',
      Timesheets: 'ASC',
      Trips: 'ASC',
      Spiffs: 'ASC',
      Reimbursements: 'ASC',
    },
  });

  const loadEvent = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_LOADING_EVENT, data: true });

    const event = await EventClientService.LoadEventByServiceCallID(serviceCallId);
    if (event) {
      dispatch({ type: ACTIONS.SET_EVENT, data: event });
      dispatch({ type: ACTIONS.SET_LOADING_EVENT, data: false });
    }
  }, [serviceCallId]);

  const loadInit = useCallback(async () => {
    await loadEvent();
    dispatch({ type: ACTIONS.SET_LOADED_INIT, data: true });
  }, [loadEvent]);
  const sortTransactions = useCallback(
    (transactions: Transaction[], sortDir: OrderDir, sortBy: string) => {
      let temp = transactions;
      if (sortBy === 'timestamp') {
        temp = temp.sort((a, b) => {
          const dateA = new Date(a.timestamp.split(' ')[0]);
          const dateB = new Date(b.timestamp.split(' ')[0]);

          if (sortDir === 'ASC') {
            return dateA.valueOf() - dateB.valueOf();
          } else {
            return dateB.valueOf() - dateA.valueOf();
          }
        });
      }

      if (sortBy === 'amount') {
        temp = temp.sort((a, b) => {
          if (sortDir === 'ASC') {
            return a.amount - b.amount;
          } else {
            return b.amount - a.amount;
          }
        });
      }

      if (sortBy === 'cost_center_id') {
        temp = temp.sort((a, b) => {
          if (sortDir === 'ASC') {
            return a.costCenterId - b.costCenterId;
          } else {
            return b.costCenterId - a.costCenterId;
          }
        });
      }

      if (sortBy === 'job_number') {
        temp = temp.sort((a, b) => {
          if (sortDir === 'ASC') {
            return a.jobId - b.jobId;
          } else {
            return b.jobId - a.jobId;
          }
        });
      }
      if (sortBy === 'owner_name') {
        temp = temp.sort((a, b) => {
          if (sortDir === 'ASC') {
            const splitA = a.ownerName.split(' ');
            const splitB = b.ownerName.split(' ');
            const lastA = splitA[splitA.length - 1].toLowerCase();
            const lastB = splitB[splitB.length - 1].toLowerCase();
            const firstA = splitA[0].toLowerCase();
            const firstB = splitB[0].toLowerCase();

            if (lastA + firstA < lastB + firstB) {
              return -1;
            }

            if (lastA + firstA > lastB + firstB) {
              return 1;
            }
            return 0;
          } else {
            const splitA = a.ownerName.split(' ');
            const splitB = b.ownerName.split(' ');
            const lastA = splitA[splitA.length - 1].toLowerCase();
            const lastB = splitB[splitB.length - 1].toLowerCase();
            const firstA = splitA[0].toLowerCase();
            const firstB = splitB[0].toLowerCase();

            if (lastA + firstA > lastB + firstB) {
              return -1;
            }

            if (lastA + firstA < lastB + firstB) {
              return 1;
            }
            return 0;
          }
        });
      }
      if (sortBy === 'description') {
        temp = temp.sort((a, b) => {
          if (sortDir === 'ASC') {
            return a.vendor.localeCompare(b.vendor);
          } else {
            return b.vendor.localeCompare(a.vendor);
          }
        });
      }
      return temp;
    },
    [],
  );
  const load = useCallback(async () => {
    const promises: Promise<void>[] = [];
    let timesheets: TimesheetLine[] = [];
    let classCodes: ClassCode[] = [];
    let perDiems: PerDiem[] = [];
    let transactions: Transaction[] = [];
    let trips: Trip[] = [];
    let tasks: Task[] = [];
    let invoice: Invoice | undefined = undefined;
    let materials: Quotable[] = [];
    let payments: Payment[] = [];
    let reimbursements: Reimbursement[] = [];

    dispatch({ type: ACTIONS.SET_LOADING, data: true });
    promises.push(
      new Promise<void>(async (resolve) => {
        try {
          const classCodeReq = ClassCode.create();

          classCodes = (await ClassCodeClientService.BatchGet(classCodeReq))!.results;
          dispatch({
            type: ACTIONS.SET_CLASS_CODES,
            data: classCodes,
          });
          const mappedResults = classCodes.map((code) => ({
            classCodeId: code.id,
            active: 0,
          }));
          dispatch({
            type: ACTIONS.SET_CLASS_CODE_DROPDOWNS,
            data: mappedResults,
          });
          resolve();
        } catch (err) {
          console.log('error fetching class codes', err);
          setLoadFailed(true);
        }
      }),
    );
    promises.push(
      new Promise<void>(async (resolve) => {
        try {
          const userReq = User.create();
          userReq.isEmployee = 1;
          userReq.overrideLimit = true;
          const users = (await UserClientService.BatchGet(userReq)).results;
          dispatch({
            type: ACTIONS.SET_USERS,
            data: users,
          });
          resolve();
        } catch (err) {
          console.log('error fetching users', err);
          setLoadFailed(true);
        }
      }),
    );
    promises.push(
      new Promise<void>(async (resolve) => {
        try {
          const perDiemReq = PerDiem.create();
          perDiemReq.withRows = true;
          perDiemReq.isActive = true;
          perDiemReq.withoutLimit = true;
          const row = PerDiemRow.create();
          row.serviceCallId = serviceCallId;
          perDiemReq.referenceRow = row;
          const taskReq = Task.create();
          taskReq.eventId = serviceCallId;
          taskReq.billable = 1;
          taskReq.isActive = true;
          taskReq.withoutLimit = true;
          const timesheetReq = TimesheetLine.create();
          //Currently using custom sql, so only Reference number matters
          timesheetReq.withoutLimit = true;
          timesheetReq.referenceNumber = serviceCallId.toString();
          timesheetReq.isActive = 1;
          timesheetReq.orderBy = 'time_started';
          timesheetReq.orderDir = 'ASC';
          const tripReq = Trip.create();
          tripReq.isActive = true;
          tripReq.jobNumber = serviceCallId;
          tripReq.withoutLimit = true;
          const transactionReq = Transaction.create();
          transactionReq.jobId = serviceCallId;
          transactionReq.isActive = 1;
          transactionReq.withoutLimit = true;
          const reportReq = CostReportReq.create();
          reportReq.perDiemReq = perDiemReq;
          reportReq.taskReq = taskReq;
          reportReq.timesheetReq = timesheetReq;
          reportReq.transactionReq = transactionReq;
          reportReq.tripReq = tripReq;

          try {
            const allResults = await EventClientService.BatchGetCostReportData(reportReq);
            if (allResults) {
              if (allResults.perDiemResults !== undefined) {
                perDiems = allResults.perDiemResults.results;
              }
              if (allResults.taskResults !== undefined) {
                tasks = allResults.taskResults.results;
              }
              if (allResults.timesheetResults !== undefined) {
                timesheets = allResults.timesheetResults.results;
              }
              if (allResults.tripResults !== undefined) {
                trips = allResults.tripResults.results;
              }
              if (allResults.transactionResults !== undefined) {
                transactions = allResults.transactionResults.results;
              }
            }
          } catch (err) {
            console.log('error with fetch data function:', err);
            setLoadFailed(true);
          }

          resolve();
        } catch (err) {
          console.log('error fetching data', err);
          setLoadFailed(true);
        }
      }),
    );
    promises.push(
      new Promise<void>(async (resolve) => {
        const accountReq = TransactionAccount.create();

        try {
          const accountRes = (await TransactionAccountClientService.BatchGet(accountReq))!.results;
          dispatch({
            type: ACTIONS.SET_TRANSACTION_ACCOUNTS,
            data: accountRes,
          });

          const costCenterMap = accountRes.map((account) => ({
            costCenterId: account.id,
            active: 0,
          }));
          dispatch({
            type: ACTIONS.SET_TRANSACTION_DROPDOWNS,
            data: costCenterMap,
          });
          resolve();
        } catch (err) {
          console.log('error fetching accounts,', err);
          setLoadFailed(true);
        }
      }),
    );
    promises.push(
      new Promise<void>(async (resolve) => {
        try {
          const invoiceReq = Invoice.create();
          invoiceReq.eventId = serviceCallId;
          const invoiceResults = await InvoiceClientService.BatchGet(invoiceReq);
          if (invoiceResults && invoiceResults.results.length > 0) {
            invoice = invoiceResults.results[0];
          }
          resolve();
        } catch (err) {
          console.log('error fetching invoice', err);
          setLoadFailed(true);
          resolve();
        }
      }),
    );
    promises.push(
      new Promise<void>(async (resolve) => {
        try {
          const materialReq = QuotableRead.create();
          materialReq.eventId = serviceCallId;
          materialReq.withoutLimit = true;
          const materialResults = await EventClientService.ReadQuotes(materialReq);
          if (materialResults && materialResults.data) {
            materials = materialResults.data;
          }
          resolve();
        } catch (err) {
          console.log('error fetching materials', err);
          setLoadFailed(true);
          resolve();
        }
      }),
    );
    promises.push(
      new Promise<void>(async (resolve) => {
        try {
          const req = Int32.create();
          req.value = serviceCallId;
          const paymentResults = await PaymentClientService.GetPaymentsByEventID(req);
          if (paymentResults && paymentResults.results) {
            payments = paymentResults.results;
          }
          resolve();
        } catch (err) {
          console.log('error fetching payments', err);
          setLoadFailed(true);
          resolve();
        }
      }),
    );
    promises.push(
      new Promise<void>(async (resolve) => {
        try {
          const reimbursementReq = Reimbursement.create();
          reimbursementReq.jobNumber = serviceCallId;
          reimbursementReq.isActive = 1;
          reimbursementReq.withoutLimit = true;
          const reimbursementResults = await ReimbursementClientService.BatchGet(reimbursementReq);
          if (reimbursementResults && reimbursementResults.results) {
            reimbursements = reimbursementResults.results;
          }
          resolve();
        } catch (err) {
          console.log('error fetching reimbursements', err);
          setLoadFailed(true);
          resolve();
        }
      }),
    );
    Promise.all(promises).then(async () => {
      console.log('all promises executed without error, setting loaded');

      let allTripsTotal = 0;
      trips.forEach((trip) => {
        // Subtracting 30 miles flat from trip distance in accordance

        allTripsTotal += trip.distanceInMiles * IRS_SUGGESTED_MILE_FACTOR;
      });
      const costCenterTemp: { [key: string]: number } = {};
      for (let i = 0; i < transactions.length; i++) {
        if (!transactionCounts(transactions[i])) continue;
        const keyValue = `${transactions[i].costCenterId}-${transactions[i].costCenter?.description}`;
        if (costCenterTemp[keyValue]) {
          costCenterTemp[keyValue] += transactions[i].amount;
        } else {
          //
          costCenterTemp[keyValue] = transactions[i].amount;
        }
      }
      dispatch({ type: ACTIONS.SET_COST_CENTER_TOTALS, data: costCenterTemp });

      dispatch({ type: ACTIONS.SET_TRIPS_TOTAL, data: allTripsTotal });
      const mappedResults = perDiems.map((perdiem) => ({
        perDiemId: perdiem.id,
        active: 0,
      }));
      dispatch({ type: ACTIONS.SET_DROPDOWNS, data: mappedResults });
      const arr: PerDiem[] = [];
      perDiems.forEach((result) => {
        let isIncluded = false;
        arr.forEach((arrItem) => {
          if (arrItem.id == result.id) isIncluded = true;
        });
        if (!isIncluded) {
          arr.push(result);
        }
      });
      for (let i = 0; i < arr.length; i++) {
        const tempRowList = arr[i].rows.filter((row) => row.serviceCallId === serviceCallId);
        arr[i].rows = tempRowList;
      }
      for (let i = 0; i < timesheets.length; i++) {
        timesheets[i].hoursWorked = roundNumber(
          differenceInMinutes(
            parseISO(timesheets[i].timeFinished),
            parseISO(timesheets[i].timeStarted),
          ) / 60,
        );
      }

      if (timesheets.length > 0) {
        const weekList: WeekClassCodeBreakdownSubtotal[] = [];

        for (let i = 0; i < timesheets.length; i++) {
          let startWeek = new Date(parseISO(timesheets[i].timeStarted));
          startWeek = startOfWeek(startWeek, { weekStartsOn: 6 });
          let endWeek = new Date(parseISO(timesheets[i].timeStarted));
          endWeek = endOfWeek(endWeek, { weekStartsOn: 6 });
          const weekStruct = {
            weekStart: format(startWeek, 'yyyy-MM-dd'),
            weekEnd: format(endWeek, 'yyyy-MM-dd'),
            employeeId: timesheets[i].technicianUserId,
            hoursSubtotal: timesheets[i].hoursWorked,
            classCodeId: timesheets[i].classCodeId,
          };
          const findExisting = weekList.findIndex(
            (week) =>
              week.classCodeId == weekStruct.classCodeId &&
              week.weekEnd == weekStruct.weekEnd &&
              week.weekStart === weekStruct.weekStart &&
              week.employeeId == weekStruct.employeeId,
          );
          if (findExisting != -1) {
            weekList[findExisting].hoursSubtotal += weekStruct.hoursSubtotal;
          } else {
            weekList.push(weekStruct);
          }
        }
        dispatch({
          type: ACTIONS.SET_TIMESHEET_WEEKLY_SUBTOTALS,
          data: weekList,
        });
      }

      const laborTemp = state.laborTotals;
      for (let i = 0; i < timesheets.length; i++) {
        const keyValue = timesheets[i].classCodeId.toString();
        if (laborTemp[keyValue]) {
          laborTemp[keyValue] += timesheets[i].hoursWorked;
        } else {
          laborTemp[keyValue] = timesheets[i].hoursWorked;
        }
      }
      dispatch({ type: ACTIONS.SET_LABOR_TOTALS, data: laborTemp });

      let total = 0;
      timesheets.forEach((timesheet) => (total = total + timesheet.hoursWorked));
      dispatch({
        type: ACTIONS.SET_ALL_DATA,
        data: {
          timesheetRes: timesheets,
          transactionRes: sortTransactions(transactions, 'ASC', 'amount'),
          perDiemRes: arr,
          taskRes: tasks,
          tripRes: trips,
        },
      });

      dispatch({ type: ACTIONS.SET_INVOICE, data: invoice });
      dispatch({ type: ACTIONS.SET_MATERIALS, data: materials });
      dispatch({ type: ACTIONS.SET_PAYMENTS, data: payments });
      dispatch({ type: ACTIONS.SET_REIMBURSEMENTS, data: reimbursements });
      dispatch({ type: ACTIONS.SET_TOTAL_HOURS_WORKED, data: total });
      dispatch({ type: ACTIONS.SET_LOADING, data: false });
      dispatch({ type: ACTIONS.SET_LOADED, data: true });
    });
  }, [serviceCallId, state.laborTotals, sortTransactions]);
  const createReport = (section: string) => {
    const totalMeals = state.perDiems.reduce(
      (aggr, pd) => aggr + getPerDiemTotalsFromRows(pd.rows).meals,
      0,
    );
    const perDiemLodging = state.perDiems.reduce(
      (aggr, pd) => aggr + getPerDiemTotalsFromRows(pd.rows).lodging,
      0,
    );
    // Fold company-booked hotel spend (cost center 643003) into Lodging and out of the
    // Transactions total so the same dollars are not counted twice.
    const { lodging: lodgingTravelTotal, nonLodging: totalTransactions } = getTransactionTotals(
      state.transactions.filter(transactionCounts),
    );
    const totalLodging = perDiemLodging + lodgingTravelTotal;
    const totalPayments = state.payments.reduce(
      (aggr, payment) => aggr + (payment.amountCollected || 0),
      0,
    );
    const invoiceTotal = state.invoice
      ? parseFloat(state.invoice.totalamountrow1 || '0') +
        parseFloat(state.invoice.totalamountrow2 || '0') +
        parseFloat(state.invoice.totalamountrow3 || '0') +
        parseFloat(state.invoice.totalamountrow4 || '0') +
        parseFloat(state.invoice.materialTotal || '0') -
        parseFloat(state.invoice.discountcost || '0') +
        (state.event?.taxCost || 0)
      : 0;
    let fullString = '';
    const find = ',';
    const re = new RegExp(find, 'g');
    if (section === 'JobDetails' && state.event) {
      fullString = ' Type , Cost + `\r\n`';

      const address = getPropertyAddress(state.event.property).replace(re, '');
      fullString =
        fullString +
        address +
        ',' +
        formatDate(state.event!.dateStarted) +
        formatDate(state.event!.dateEnded) +
        `\r\n`;
      const costString =
        'Type, Total \r\n Total Hours Worked ,' +
        state.totalHoursWorked +
        ' \r\n' +
        (employeeHourTotals.length > 0
          ? '\r\nHours by Employee\r\nEmployee,Total Hours\r\n' +
            employeeHourTotals
              .map(
                (employee) =>
                  employee.employeeName.replace(re, ' ') + ',' + roundNumber(employee.hours),
              )
              .join('\r\n') +
            '\r\n\r\n'
          : '') +
        ' Transactions,' +
        totalTransactions +
        '\r\n' +
        'Meals,' +
        totalMeals +
        '\r\n' +
        'Lodging,' +
        totalLodging +
        '\r\n' +
        'Spiffs,' +
        totalTasksBillable +
        '\r\n' +
        'Reimbursements,' +
        totalReimbursements +
        '\r\nTrips Total,' +
        state.tripsTotal.toFixed(2) +
        '\r\nTotal Miles,' +
        roundNumber(totalMiles) +
        '\r\n' +
        (state.invoice ? 'Invoice Total,' + usd(invoiceTotal) + '\r\n' : '') +
        (state.payments.length > 0
          ? 'Total Payments Received,' + usd(totalPayments) + '\r\n'
          : '') +
        (state.invoice && state.payments.length > 0
          ? 'Outstanding Balance,' + usd(invoiceTotal - totalPayments) + '\r\n'
          : '') +
        '\r\n ';

      fullString = costString;
    }
    if (section === 'Invoice' && state.invoice) {
      fullString = 'Invoice Report\r\n';
      fullString += 'Description,Amount\r\n';

      if (state.invoice.servicesperformedrow1) {
        fullString +=
          state.invoice.servicesperformedrow1.replace(re, ' ') +
          ',' +
          usd(parseFloat(state.invoice.totalamountrow1 || '0')) +
          '\r\n';
      }
      if (state.invoice.servicesperformedrow2) {
        fullString +=
          state.invoice.servicesperformedrow2.replace(re, ' ') +
          ',' +
          usd(parseFloat(state.invoice.totalamountrow2 || '0')) +
          '\r\n';
      }
      if (state.invoice.servicesperformedrow3) {
        fullString +=
          state.invoice.servicesperformedrow3.replace(re, ' ') +
          ',' +
          usd(parseFloat(state.invoice.totalamountrow3 || '0')) +
          '\r\n';
      }
      if (state.invoice.servicesperformedrow4) {
        fullString +=
          state.invoice.servicesperformedrow4.replace(re, ' ') +
          ',' +
          usd(parseFloat(state.invoice.totalamountrow4 || '0')) +
          '\r\n';
      }

      fullString += '\r\nMaterials\r\n';
      fullString += 'Description,Quantity,Price,Billable,Total\r\n';
      state.materials.forEach((material) => {
        const total = material.isBillable ? material.quantity * material.quotedPrice : 0;
        fullString +=
          material.description.replace(re, ' ') +
          ',' +
          material.quantity +
          ',' +
          usd(material.quotedPrice) +
          ',' +
          (material.isBillable ? 'Yes' : 'No') +
          ',' +
          usd(total) +
          '\r\n';
      });

      // Always show discount (even if 0)
      const discountAmount = parseFloat(state.invoice.discountcost || '0');
      fullString += '\r\nDiscount,' + usd(discountAmount) + '\r\n';

      // Always show tax (even if 0)
      const taxAmount = state.event?.taxCost || 0;
      fullString += 'Tax,' + usd(taxAmount) + '\r\n';

      fullString += '\r\nGrand Total,' + usd(invoiceTotal) + '\r\n';
    }
    if (section === 'Payments' && state.payments) {
      fullString = 'Payments Report\r\n';
      fullString += 'Service Rendered ID,Amount Collected,Type,Collected\r\n';
      for (let i = 0; i < state.payments.length; i++) {
        const p = state.payments[i];
        const tempString =
          p.servicesRenderedId +
          ',' +
          usd(p.amountCollected || 0) +
          ',' +
          (p.type || '-').replace(re, ' ') +
          ',' +
          (p.collected ? 'Yes' : 'No') +
          '\r\n';
        fullString = fullString + tempString;
      }
      fullString += '\r\nTotal Payments,' + usd(totalPayments) + '\r\n';
    }
    if (section == 'Timesheets') {
      fullString =
        ' Technician,Department,Approved By,Time Started,Time Ended,Description,Hours,Status,Notes' +
        `\r\n`;
      for (let i = 0; i < state.timesheets.length; i++) {
        const t = state.timesheets[i];
        const tempString =
          t.technicianUserName.replace(',', ' ') +
          ',' +
          t.departmentName.replace(',', ' ') +
          ',' +
          t.adminApprovalUserName.replace(',', ' ') +
          ',' +
          t.timeStarted.replace(',', ' ') +
          ',' +
          t.timeFinished.replace(',', ' ') +
          ',' +
          t.briefDescription.replace(',', ' ') +
          ',' +
          t.hoursWorked +
          ',' +
          getTimesheetStatus(t) +
          ',' +
          t.notes.replace(',', '') +
          `\r\n`;
        fullString = fullString + tempString;
      }
      const weeklySummary = calculateWeeklySummary(transformTimesheetsToDataList(state.timesheets));
      if (weeklySummary.length > 0) {
        fullString += '\r\nWeekly Hours Summary\r\n';
        fullString += 'Week Start,Week End,Employee,Regular Hours,Overtime Hours,Total Hours\r\n';
        for (let i = 0; i < weeklySummary.length; i++) {
          const s = weeklySummary[i];
          fullString +=
            format(parseISO(s.weekStart), 'MM/dd/yyyy') +
            ',' +
            format(parseISO(s.weekEnd), 'MM/dd/yyyy') +
            ',' +
            s.employeeName.replace(re, ' ') +
            ',' +
            s.regularHours.toFixed(2) +
            ',' +
            s.overtimeHours.toFixed(2) +
            ',' +
            s.totalHours.toFixed(2) +
            `\r\n`;
        }
      }
    }
    if (section == 'Transactions' && state.transactions) {
      fullString =
        'Transaction Report' +
        `\r\n` +
        ' Department,Owner,Cost Center / Vendor,Date,Amount,Status,Notes' +
        `\r\n`;
      for (let i = 0; i < state.transactions.length; i++) {
        const t = state.transactions[i];
        const tempString =
          t.department?.classification.replace(',', ' ') +
          '-' +
          t.department?.description.replace(',', ' ') +
          ',' +
          t.ownerName.replace(',', '') +
          ',' +
          t.costCenter?.description +
          '-' +
          t.vendor.replace(',', ' ') +
          ',' +
          formatDate(t.timestamp) +
          ',' +
          usd(t.amount) +
          ',' +
          getTransactionStatus(t) +
          ',' +
          t.notes.replace(',', ' ') +
          `\r\n`;
        fullString = fullString + tempString;
      }
    }
    if (section == 'PerDiems' && state.perDiems) {
      fullString =
        ' Department,Owner,Submitted Date,Approved By,Approved Date,Total Meals,Total Lodging,Status,Notes' +
        `\r\n`;
      for (let i = 0; i < state.perDiems.length; i++) {
        const t = state.perDiems[i];

        const tempString =
          TimesheetDepartmentClientService.getDepartmentName(t.department!).replace(re, '') +
          ',' +
          t.ownerName +
          ',' +
          (t.dateSubmitted != NULL_TIME ? formatDate(t.dateSubmitted) : '-') +
          ',' +
          (t.approvedByName || '-') +
          ',' +
          (t.dateApproved != NULL_TIME ? formatDate(t.dateApproved) : '-') +
          ',' +
          (() => {
            const { meals, lodging } = getPerDiemTotalsFromRows(t.rows);
            return usd(meals) + ',' + (lodging !== 0 ? usd(lodging) : '-');
          })() +
          ',' +
          getPerDiemStatus(t) +
          ',' +
          t.notes +
          `\r\n`;
        fullString = fullString + tempString;
      }
    }
    if (section == 'Trips' && state.trips) {
      fullString = 'Date,Origin,Destination,Distance (in Miles),Home Travel,Cost,Status,Notes\r\n';
      for (let i = 0; i < state.trips.length; i++) {
        const t = state.trips[i];
        const tempString =
          formatDate(t.date) +
          ',' +
          t.originAddress.replace(re, ' ') +
          ',' +
          t.destinationAddress.replace(re, ' ') +
          ',' +
          t.distanceInMiles +
          ',' +
          (t.homeTravel == true ? 'Yes' : 'No') +
          ',' +
          usd(Number((t.distanceInMiles * IRS_SUGGESTED_MILE_FACTOR).toFixed(2))) +
          ',' +
          getTripStatus(t) +
          ',' +
          t.notes.replace(re, '') +
          `\r\n`;
        fullString = fullString + tempString;
      }
      fullString += 'Total Miles,' + roundNumber(totalMiles) + '\r\n';
    }
    if (section == 'Spiffs' && state.tasks) {
      fullString = 'Date Performed,Type,Details,Flat Rate?,Amount,Status, Notes\r\n';
      for (let i = 0; i < state.tasks.length; i++) {
        const t = state.tasks[i];
        const tempString =
          formatDate(t.datePerformed) +
          ',' +
          t.billableType.replace(re, ' ') +
          ',' +
          t.details.replace(re, ' ') +
          ',' +
          t.flatRate +
          ',' +
          usd(t.billableType === 'Spiff' ? t.spiffAmount : t.toolpurchaseCost) +
          ',' +
          getSpiffStatus(t) +
          ',' +
          t.notes.replace(re, '') +
          `\r\n`;
        fullString = fullString + tempString;
      }
    }
    if (section == 'Reimbursements' && state.reimbursements) {
      fullString = 'Date,Owner,Description,Reviewed By,Amount,Status\r\n';
      for (let i = 0; i < state.reimbursements.length; i++) {
        const r = state.reimbursements[i];
        const tempString =
          formatDate(r.createdDate) +
          ',' +
          r.ownerName.replace(re, ' ') +
          ',' +
          r.description.replace(re, ' ') +
          ',' +
          (r.reviewerName || '-').replace(re, ' ') +
          ',' +
          usd(r.amount) +
          ',' +
          getReimbursementStatus(r) +
          `\r\n`;
        fullString = fullString + tempString;
      }
    }
    return fullString;
  };
  const downloadPageCSV = () => {
    downloadCSV(state.activeTab + ' Report For ' + serviceCallId, createReport(state.activeTab));
  };
  const downloadFullReportCSV = () => {
    const header = state.event
      ? 'Cost Report\r\nJob Number,' + state.event.logJobNumber + '\r\n\r\n'
      : 'Cost Report\r\n\r\n';
    const sectionLabels: Record<(typeof tabs)[number], string> = {
      JobDetails: 'Job Details',
      Invoice: 'Invoice & Materials',
      Payments: 'Payments',
      Transactions: 'Transactions',
      PerDiems: 'Per Diems',
      Timesheets: 'Timesheets',
      Trips: 'Trips',
      Spiffs: 'Spiffs',
      Reimbursements: 'Reimbursements',
    };
    const body = tabs
      .map((section) => {
        const sectionString = createReport(section);
        return sectionString && sectionString.trim()
          ? sectionLabels[section] + '\r\n' + sectionString
          : '';
      })
      .filter(Boolean)
      .join('\r\n');
    downloadCSV('Cost Report For ' + serviceCallId, header + body);
  };
  const downloadReceiptsZipMutation = useDownloadTransactionFilesZipMutation({
    onSuccess: (fileCount) => {
      if (fileCount === 0) {
        toast({
          title: 'No Receipts',
          description: 'No receipt files were found for the transactions on this job.',
          variant: 'default',
        });
        return;
      }
      toast({
        title: 'Download Started',
        description: 'Transaction receipts are being downloaded as a zip file.',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Download Failed',
        description: 'There was an error downloading the receipts. Please try again.',
        variant: 'destructive',
      });
    },
  });
  const downloadReceiptsZip = () => {
    downloadReceiptsZipMutation.mutate({
      jobId: serviceCallId,
      filename: `${serviceCallId}-files-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.zip`,
    });
  };
  useEffect(() => {
    if (!state.loadedInit) {
      loadInit().catch(() => setLoadFailed(true));
    }
    if (state.loadedInit == true && state.loaded === false) {
      load();
    }
  }, [state.loadedInit, loadInit, state.loaded, load]);
  const costCenterArray = [['', '']];
  const laborCostArray = [['', '']];
  state.transactionAccounts.forEach((account) => {
    // Lodging/Travel (643003) is reported under Lodging, not in the Transactions breakdown.
    if (account.id === LODGING_TRAVEL_COST_CENTER_ID) return;
    const findAccount = `${account.id}-${account.description}`;
    if (state.costCenterTotals[findAccount]) {
      const value = [findAccount, usd(state.costCenterTotals[findAccount])];
      costCenterArray.push(value);
    }
  });

  state.classCodes.forEach((code) => {
    const findAccount = code.id;
    if (state.laborTotals[findAccount]) {
      const value = [code.description, `${state.laborTotals[findAccount]} hour(s)`];
      laborCostArray.push(value);
    }
  });

  const employeeHourTotals: { employeeId: number; employeeName: string; hours: number }[] = [];
  state.timesheets.forEach((timesheet) => {
    const existing = employeeHourTotals.find(
      (employee) => employee.employeeId === timesheet.technicianUserId,
    );
    if (existing) {
      existing.hours += timesheet.hoursWorked;
    } else {
      employeeHourTotals.push({
        employeeId: timesheet.technicianUserId,
        employeeName: timesheet.technicianUserName || 'Unknown',
        hours: timesheet.hoursWorked,
      });
    }
  });
  employeeHourTotals.sort((a, b) => a.employeeName.localeCompare(b.employeeName));

  const totalMeals = state.perDiems.reduce(
    (aggr, pd) => aggr + getPerDiemTotalsFromRows(pd.rows).meals,
    0,
  );
  const perDiemLodging = state.perDiems.reduce(
    (aggr, pd) => aggr + getPerDiemTotalsFromRows(pd.rows).lodging,
    0,
  );
  // Fold company-booked hotel spend (cost center 643003) into Lodging and out of the
  // Transactions total so the same dollars are not counted twice.
  const { lodging: lodgingTravelTotal, nonLodging: totalTransactions } = getTransactionTotals(
    state.transactions.filter(transactionCounts),
  );
  const totalLodging = perDiemLodging + lodgingTravelTotal;
  const totalTasksBillable = state.tasks
    .filter(spiffCounts)
    .reduce((aggr, pd) => aggr + pd.spiffAmount, 0);
  const totalReimbursements = state.reimbursements
    .filter(reimbursementCounts)
    .reduce((aggr, r) => aggr + r.amount, 0);
  const totalMiles = state.trips.reduce((aggr, trip) => aggr + trip.distanceInMiles, 0);

  const dateSortColumn = (tab: DateSortTab) => ({
    dir: state.dateSort[tab],
    onClick: () =>
      dispatch({
        type: ACTIONS.SET_DATE_SORT,
        data: { tab, dir: state.dateSort[tab] === 'ASC' ? 'DESC' : 'ASC' },
      }),
  });
  const sortedPerDiems = [...state.perDiems].sort((a, b) =>
    compareDates(a.dateSubmitted, b.dateSubmitted, state.dateSort.PerDiems),
  );
  const sortedTimesheets = [...state.timesheets].sort((a, b) =>
    compareDates(a.timeStarted, b.timeStarted, state.dateSort.Timesheets),
  );
  const sortedTrips = [...state.trips].sort((a, b) =>
    compareDates(a.date, b.date, state.dateSort.Trips),
  );
  const sortedTasks = [...state.tasks].sort((a, b) =>
    compareDates(a.datePerformed, b.datePerformed, state.dateSort.Spiffs),
  );
  const sortedReimbursements = [...state.reimbursements].sort((a, b) =>
    compareDates(a.createdDate, b.createdDate, state.dateSort.Reimbursements),
  );

  const weekArray: Week[] = [];

  for (let i = 0; i < state.timesheetWeeklySubtotals.length; i++) {
    const temp = state.timesheetWeeklySubtotals[i];
    const found = weekArray.find(
      (week) => temp.weekStart == week.weekStart && week.code == temp.classCodeId,
    );
    if (found == undefined) {
      weekArray.push({
        weekEnd: temp.weekEnd,
        weekStart: temp.weekStart,
        code: temp.classCodeId,
      });
    }
  }

  /** Starts the load again from the event, after a failure. */
  const retryLoad = useCallback(() => {
    setLoadFailed(false);
    dispatch({ type: ACTIONS.SET_LOADED, data: false });
    dispatch({ type: ACTIONS.SET_LOADED_INIT, data: false });
  }, []);

  return {
    loadFailed,
    retryLoad,
    state,
    dispatch,
    loadEvent,
    loadInit,
    sortTransactions,
    load,
    createReport,
    downloadPageCSV,
    downloadFullReportCSV,
    downloadReceiptsZipMutation,
    downloadReceiptsZip,
    costCenterArray,
    laborCostArray,
    employeeHourTotals,
    totalMeals,
    perDiemLodging,
    lodgingTravelTotal,
    totalTransactions,
    totalLodging,
    totalTasksBillable,
    totalReimbursements,
    totalMiles,
    dateSortColumn,
    sortedPerDiems,
    sortedTimesheets,
    sortedTrips,
    sortedTasks,
    sortedReimbursements,
    weekArray,
  };
};
